import { Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';
import { PopupTemplateContainerService } from './popup-template/popup-template-container.service';
import { delay } from './utils.service';
import { PopupMenuData, TemplateObjectType } from './popup-template/interfaces';
import { Subject, debounceTime } from 'rxjs';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: '[popupMenuTriggerFor]',
    standalone: false
})
export class PopupMenuDirective<TemplateData extends {}> implements OnDestroy {
    @Input() popupMenuTriggerFor: PopupMenuData<TemplateData>['templateRef'];
    @Input() popupMenuData?: TemplateData;
    readonly createdAt: number = (new Date).getTime();
    readonly delayInterval = 50;
    readonly shouldShowMenu$ = new Subject<boolean>;
    readonly subscription = this.shouldShowMenu$.pipe(
        debounceTime(this.delayInterval + 10)
    ).subscribe(shouldShowMenu => shouldShowMenu ? this.showMenu() : this.removeMenu());

    constructor(
        private host: ElementRef<HTMLElement>,
        private popupTemplateSvc: PopupTemplateContainerService,
    ) {}

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    @HostListener('click')
    async onHostClick() {
        // `onWindowClick` will always get executed for any click event.
        // Therefore, if there is any click-event targetting the host,
        // we allow the `onWindowClick` to executes first,
        // then add some `delayInterval` before executing `showMenu`
        await delay(this.delayInterval);
        this.shouldShowMenu$.next(true);
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.shouldShowMenu$.next(false);
    }

    /** Handle click outside element */
    @HostListener('window:click')
    onWindowClick() {
        this.shouldShowMenu$.next(false);
    }

    private showMenu() {
        if (this.popupMenuTriggerFor) {
            this.popupTemplateSvc.addTemplate({
                templateRef: this.popupMenuTriggerFor,
                templateData: this.popupMenuData,
                identifier: this.createdAt,
                type: TemplateObjectType.menu,
                triggerHost: this.host
            });
        }
    }

    private removeMenu() {
        this.popupTemplateSvc.removeTemplate({
            type: TemplateObjectType.menu,
            identifier: this.createdAt
        });
    }
}
